import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { CalendarDays, CheckCircle2, Circle } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";

const iso = (d) => format(d, "yyyy-MM-dd");
const toDate = (s) => new Date(`${s}T00:00:00`);
const linkOf = (a) => ((a.type === "lesson" || a.type === "countalong") && a.lesson_id ? `/lessons/${a.lesson_id}` : `/courses/${a.instrument_id}/${a.course_id}`);

// Pupil's month view of homework due dates; tap a day to see what's due and whether it's done.
export const PupilHomeworkCalendar = ({ items }) => {
  const dated = useMemo(() => items.filter((a) => a.due_date), [items]);
  const byDay = useMemo(() => dated.reduce((m, a) => ({ ...m, [a.due_date]: [...(m[a.due_date] || []), a] }), {}), [dated]);
  const firstTodo = dated.filter((a) => !a.done && a.due_date >= iso(new Date())).sort((x, y) => x.due_date.localeCompare(y.due_date))[0];
  const [day, setDay] = useState(firstTodo ? toDate(firstTodo.due_date) : new Date());
  const [month, setMonth] = useState(day);
  if (dated.length === 0) return null;
  const list = byDay[iso(day)] || [];
  const days = (done) => Object.keys(byDay).filter((d) => byDay[d].every((a) => a.done) === done).map(toDate);
  return (
    <section className="mt-4 rounded-2xl border border-border bg-card p-5" data-testid="pupil-homework-calendar">
      <h3 className="flex items-center gap-2 font-serif text-lg"><CalendarDays size={17} className="text-primary" /> Homework calendar</h3>
      <div className="mt-3 flex flex-col gap-5 md:flex-row md:items-start">
        <Calendar mode="single" selected={day} onSelect={(d) => d && setDay(d)} month={month} onMonthChange={setMonth} data-testid="pupil-homework-month"
          modifiers={{ todo: days(false), done: days(true) }}
          modifiersClassNames={{ todo: "font-bold text-primary underline decoration-2 underline-offset-4", done: "text-emerald-600 dark:text-emerald-400 line-through" }}
          className="shrink-0 self-center rounded-xl border border-border bg-background md:self-start" />
        <div className="min-w-0 flex-1" data-testid="pupil-homework-day">
          <p className="font-mono text-[11px] uppercase tracking-widest text-primary" data-testid="pupil-homework-day-label">Due {format(day, "EEEE d MMMM")}</p>
          {list.length === 0 ? <p className="mt-3 text-sm text-muted-foreground" data-testid="pupil-homework-day-empty">Nothing due on this day.</p> : (
            <ul className="mt-3 space-y-2">
              {list.map((a) => (
                <li key={a.id}>
                  <Link to={linkOf(a)} data-testid={`pupil-homework-item-${a.id}`} data-done={!!a.done}
                    className="flex items-start gap-2 rounded-xl border border-border bg-background px-4 py-3 text-sm transition-colors hover:border-primary">
                    {a.done ? <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" /> : <Circle size={16} className="mt-0.5 shrink-0 text-muted-foreground" />}
                    <span>
                      <span className="font-medium">{a.title}</span>
                      <span className="block text-xs text-muted-foreground">{a.class_name || "Your class"} · {a.done ? "Done" : "To do"}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-xs text-muted-foreground">Bold days still have homework to do; crossed-out days are all done.</p>
        </div>
      </div>
    </section>
  );
};
